// @flow

import type {SearchRequest} from '../models/SearchRequest';
import type {Options} from './SearchUrlParser';
import SearchUrlParser from './SearchUrlParser';
import {intParser} from '../utils';
import querystring from 'query-string';
import {decodeFilterValue} from '../parsers/filterRefinementUtils';

import {
    REGION,
    MIN_BATHROOMS,
    MAX_BATHROOMS,
    MIN_SLEEPS,
    MAX_SLEEPS,
    MIN_BEDROOMS,
    MAX_BEDROOMS,
    MIN_NIGHTLY_PRICE,
    MAX_NIGHTLY_PRICE,
    FILTER
} from '../models/SearchUrlParameters';

import {
    BOUNDING_BOX_REGEX,
    REGION_COLON_PARAMETER_VARIANTS,
    LANDING_PAGE_URL_PATTERN,
    SEARCH_URL_PARAMETERS,
    SLASH_PARAMETERS,
    COLON_PARAMETERS
} from '../models/SearchUrl';

const isLandingPageUrl = (path: string) =>
    path.match(LANDING_PAGE_URL_PATTERN) !== null;

export default class GTSearchUrlParser extends SearchUrlParser {
    parse(url: string, options: Options = {}): SearchRequest {
        const parts: string[] = this.getUrlParts(url);
        let searchRequest: SearchRequest = {};

        if (!this.isEmpty(parts[1])) {
            if (isLandingPageUrl(parts[1])) {
                this.parseLandingUrl(parts[1], searchRequest);
            }

            if (parts[1].includes('?')) {
                const pair: string[] = parts[1].split('?');

                this.parseQueryParams(
                    searchRequest,
                    options,
                    // adds filter refinements to searchRequest.filter
                    this.getPathParams(pair[0], searchRequest, options)
                );
                this.parseQueryParams(
                    searchRequest,
                    options,
                    querystring.parseUrl(parts[1]).query
                );
            } else {
                this.parseQueryParams(
                    searchRequest,
                    options,
                    this.getPathParams(parts[1], searchRequest, options)
                );
            }
        }

        return searchRequest;
    }

    getPathParams(
        path: string,
        searchRequest: SearchRequest,
        options: Options
    ): Object {
        const segments: string[] = path.split('/');
        return segments.reduce(
            (parameters: Object, segment: string, key: number): Object => {
                const tokens = segment.split(':');
                const parameterKey = tokens[0];
                const parameterValue = tokens[1];
                const colonOrSlashParameterKey = [
                    ...COLON_PARAMETERS,
                    ...SLASH_PARAMETERS
                ].filter((p) => {
                    return (
                        p === parameterKey ||
                        p.toLowerCase() === parameterKey.trim().toLowerCase()
                    );
                })[0];

                if (
                    tokens.length === 2 &&
                    COLON_PARAMETERS.includes(colonOrSlashParameterKey)
                ) {
                    parameters[colonOrSlashParameterKey] = parameterValue;
                } else if (
                    tokens.length === 1 &&
                    SLASH_PARAMETERS.includes(colonOrSlashParameterKey)
                ) {
                    const value: ?string =
                        segments.length >= key + 1 ? segments[key + 1] : null;

                    if (value) {
                        parameters[colonOrSlashParameterKey] = value;
                    }
                } else if (segment.match(BOUNDING_BOX_REGEX)) {
                    this.parseBoundingBoxPath(parameters, segment, options);
                }

                this.addFilterParamsToSearchRequest(parameters, tokens);

                if (this.isRegionPathParam(parameterKey)) {
                    parameters[REGION] = parameterValue;
                }

                return parameters;
            },
            {}
        );
    }

    parseBoundingBoxPath(
        parameters: Object,
        segment: string,
        options: Options
    ) {
        const strippedSegment = segment.replace('@', '').replace('z', '');
        const mapTokens = strippedSegment.split(',');
        if (!(mapTokens.length === 4 || mapTokens.length === 5)) {
            if (options.onWarning) {
                options.onWarning(`Malformed URL Map Segment = "${segment}"`);
            }
        } else {
            parameters.swLat = mapTokens[0];
            parameters.swLong = mapTokens[1];
            parameters.neLat = mapTokens[2];
            parameters.neLong = mapTokens[3];
            if (mapTokens[4]) {
                parameters.zoom = mapTokens[4];
            }
        }
    }

    parseLandingUrl(path: string, searchRequest: SearchRequest) {
        const matches = LANDING_PAGE_URL_PATTERN.exec(path);

        if (matches !== null) {
            if (matches[1] && this.isValidPathname(matches[1])) {
                this.setLegacyRegionPath(
                    searchRequest,
                    this.parsePathnameSegments(matches[1])
                );
            }
            searchRequest.regionId = matches[2];
        }
    }

    addFilterParamsToSearchRequest(params: Object, tokens: Array<string>) {
        let filters = params.filter || [];
        let validToken = tokens.length === 2;
        const handledParams = SEARCH_URL_PARAMETERS.concat(
            REGION_COLON_PARAMETER_VARIANTS
        ).map((k) => k.toLowerCase());

        const legacyFilter = !handledParams.includes(tokens[0].toLowerCase());
        const consolidatedFilter = tokens[0].toLowerCase() === FILTER;

        if (validToken && (legacyFilter || consolidatedFilter)) {
            filters.push(
                `${decodeFilterValue(tokens[0])}:${decodeFilterValue(
                    tokens[1]
                )}`
            );
        }

        // add to filters to searchRequest
        if (filters.length) {
            params.filter = filters;
        }
    }

    setSleeps(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[MIN_SLEEPS]) {
            let min: number = intParser(queryParameters[MIN_SLEEPS]);

            if (min) {
                searchRequest.minSleeps = min;
            }
        }

        if (queryParameters[MAX_SLEEPS]) {
            let max: number = intParser(queryParameters[MAX_SLEEPS]);

            if (max) {
                searchRequest.maxSleeps = max;
            }
        }
    }

    setBathrooms(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[MIN_BATHROOMS]) {
            let min: number = intParser(queryParameters[MIN_BATHROOMS]);

            if (min) {
                searchRequest.minBathrooms = min;
            }
        }

        if (queryParameters[MAX_BATHROOMS]) {
            let max: number = intParser(queryParameters[MAX_BATHROOMS]);

            if (max) {
                searchRequest.maxBathrooms = max;
            }
        }
    }

    setBedrooms(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[MIN_BEDROOMS]) {
            let min: number = intParser(queryParameters[MIN_BEDROOMS]);

            if (typeof min === 'number') {
                searchRequest.minBedrooms = min;
            }
        }

        if (queryParameters[MAX_BEDROOMS]) {
            let max: number = intParser(queryParameters[MAX_BEDROOMS]);

            if (typeof max === 'number') {
                searchRequest.maxBedrooms = max;
            }
        }
    }

    setPrice(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[MIN_NIGHTLY_PRICE]) {
            let min: number = intParser(queryParameters[MIN_NIGHTLY_PRICE]);

            if (min > -1) {
                searchRequest.minPrice = min;
            }
        }

        if (queryParameters[MAX_NIGHTLY_PRICE]) {
            let max: number = intParser(queryParameters[MAX_NIGHTLY_PRICE]);

            if (max > -1) {
                searchRequest.maxPrice = max;
            }
        }
    }
}
